import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Check,
    X,
    Sparkles,
    Crown,
    Building2,
    Loader2,
    ArrowRight,
    Zap
} from 'lucide-react';
import api from '../services/api';
import { trackConversionEvent } from '../utils/telemetry';

const PricingPage = ({ onClose, currentPlan = 'Free', source = 'direct' }) => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [checkoutLoading, setCheckoutLoading] = useState(null);

    useEffect(() => {
        fetchPlans();
        trackConversionEvent('pricing_page_opened', { source });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchPlans = async () => {
        try {
            const response = await api.get('/billing/plans');
            setPlans(response.data);
        } catch (err) {
            console.error('Failed to fetch plans:', err);
            // Fallback plans
            setPlans([
                {
                    id: 'free',
                    name: 'Free',
                    price: 0,
                    features: [
                        'Standard Teacher Chat',
                        'Basic lesson planning',
                        'Email integration',
                        'Voice cloning (Clear Instruction style)',
                    ],
                    video_quota: 0
                },
                {
                    id: 'pro',
                    name: 'Pro',
                    price: 9.99,
                    features: [
                        'Everything in Free',
                        'Enhanced Teacher Chat',
                        'Deeper instructional reasoning',
                        '3 voice styles + custom voice cloning',
                        '5 AI avatar videos/month',
                        'Analytics dashboard',
                    ],
                    video_quota: 5
                },
                {
                    id: 'premium',
                    name: 'Premium',
                    price: 29.99,
                    features: [
                        'Everything in Pro',
                        'Premium Teacher Chat',
                        '4 voice styles including Calm Directions',
                        'Premium audio quality + longer scripts',
                        'Unlimited AI avatar videos',
                        'Priority support',
                    ],
                    video_quota: -1
                }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const handleCheckout = async (planId) => {
        if (planId === 'free') return;

        setCheckoutLoading(planId);
        trackConversionEvent('pricing_checkout_started', { plan_id: planId, source });

        try {
            const response = await api.post('/billing/checkout', {
                plan: planId,
                source: source,  // Phase 3B: attribution through checkout
            });

            // Redirect to Stripe Checkout
            window.location.href = response.data.checkout_url;
        } catch (err) {
            console.error('Checkout failed:', err);
            alert('Failed to start checkout. Please try again.');
        } finally {
            setCheckoutLoading(null);
        }
    };

    const getPlanIcon = (planId) => {
        switch (planId) {
            case 'pro':
                return <Crown className="w-6 h-6" />;
            case 'premium':
                return <Building2 className="w-6 h-6" />;
            default:
                return <Sparkles className="w-6 h-6" />;
        }
    };

    const getPlanColor = (planId) => {
        switch (planId) {
            case 'pro':
                return 'from-amber-400 to-orange-500';
            case 'premium':
                return 'from-purple-500 to-indigo-600';
            default:
                return 'from-slate-400 to-slate-500';
        }
    };

    const isCurrentPlan = (planId) => {
        return currentPlan.toLowerCase() === planId.toLowerCase();
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-3xl w-full max-w-5xl shadow-2xl overflow-hidden my-8"
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white px-8 py-8 text-center relative">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                        <X size={20} />
                    </button>
                    <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-300 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
                        <Zap size={16} />
                        Upgrade Your Teaching
                    </div>
                    <h2 className="text-3xl font-bold mb-2">Choose Your Plan</h2>
                    <p className="text-slate-400 max-w-xl mx-auto">
                        Unlock powerful AI features to save time and create engaging content for your students.
                    </p>
                </div>

                {/* Plans Grid */}
                <div className="p-8">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 size={32} className="animate-spin text-slate-400" />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {plans.map((plan, idx) => (
                                <motion.div
                                    key={plan.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.1 }}
                                    className={`relative rounded-2xl border-2 p-6 transition-all
                    ${plan.id === 'pro'
                                            ? 'border-amber-400 shadow-lg shadow-amber-100 scale-105'
                                            : 'border-slate-200 hover:border-slate-300'
                                        }
                    ${isCurrentPlan(plan.id) ? 'ring-2 ring-emerald-500 ring-offset-2' : ''}
                  `}
                                >
                                    {/* Popular Badge */}
                                    {plan.id === 'pro' && (
                                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                            <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                                                MOST POPULAR
                                            </span>
                                        </div>
                                    )}

                                    {/* Plan Header */}
                                    <div className="text-center mb-6">
                                        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${getPlanColor(plan.id)} 
                                  text-white flex items-center justify-center mx-auto mb-4`}>
                                            {getPlanIcon(plan.id)}
                                        </div>
                                        <h3 className="text-xl font-bold text-slate-900">{plan.name}</h3>
                                        <div className="mt-2">
                                            <span className="text-4xl font-bold text-slate-900">
                                                ${plan.price.toFixed(2)}
                                            </span>
                                            {plan.price > 0 && (
                                                <span className="text-slate-500">/month</span>
                                            )}
                                        </div>
                                        {plan.video_quota !== 0 && (
                                            <p className="text-sm text-slate-500 mt-1">
                                                {plan.video_quota === -1 ? 'Unlimited' : plan.video_quota} AI videos/mo
                                            </p>
                                        )}
                                    </div>

                                    {/* Features */}
                                    <ul className="space-y-3 mb-6">
                                        {plan.features.map((feature, i) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                                                <Check size={18} className="text-emerald-500 shrink-0 mt-0.5" />
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>

                                    {/* CTA Button */}
                                    <button
                                        onClick={() => handleCheckout(plan.id)}
                                        disabled={isCurrentPlan(plan.id) || checkoutLoading === plan.id}
                                        className={`w-full py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2
                      ${isCurrentPlan(plan.id)
                                                ? 'bg-emerald-100 text-emerald-700 cursor-default'
                                                : plan.id === 'free'
                                                    ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                    : plan.id === 'pro'
                                                        ? 'bg-amber-500 text-white hover:bg-amber-600 shadow-lg shadow-amber-200'
                                                        : 'bg-slate-900 text-white hover:bg-slate-800'
                                            }
                      disabled:opacity-50
                    `}
                                    >
                                        {checkoutLoading === plan.id ? (
                                            <Loader2 size={20} className="animate-spin" />
                                        ) : isCurrentPlan(plan.id) ? (
                                            'Current Plan'
                                        ) : plan.id === 'free' ? (
                                            'Free Forever'
                                        ) : (
                                            <>
                                                Upgrade Now <ArrowRight size={18} />
                                            </>
                                        )}
                                    </button>
                                </motion.div>
                            ))}
                        </div>
                    )}

                    {/* Footer Note */}
                    <p className="text-center text-sm text-slate-400 mt-8">
                        Cancel anytime. No hidden fees. All prices in USD.
                    </p>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default PricingPage;
